import React, { useCallback, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AgreementSetup from 'components/AgreementSetup';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { ActivateAgreementBody } from 'models/Agreement';
import { getDataRoomRoute, getFileNameWithoutExtension } from 'utils';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import dataRoomApi from '../../dataRoomApi';
import { DATA_ROOM_NDA_UPLOAD_LOCATIONS } from '../../models/DataRoomNDA';

const DataRoomNdaSetupPage: React.FC = () => {
  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName, isNativeEsignEnabled },
    canUserManageNda,
    setDataRoom,
  } = useContext(DataRoomContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const ndasPath = getDataRoomRoute(dataRoomId, 'ndas');

  const createNda = useCallback(
    (body: ActivateAgreementBody) =>
      dataRoomApi.nda.createNativeNda(dataRoomId, {
        ...body,
        name: getFileNameWithoutExtension(body.file.name),
        uploadLocation: DATA_ROOM_NDA_UPLOAD_LOCATIONS.DATA_ROOM,
      }),
    [dataRoomId],
  );

  const finishActivation = useCallback(() => {
    popSuccess(t('agreementDataRoomNdaActivated'));
    navigate(ndasPath);
  }, [navigate, ndasPath, popSuccess, t]);

  const disableNativeEsign = useCallback(
    () => setDataRoom((prev) => ({ ...prev, isNativeEsignEnabled: false })),
    [setDataRoom],
  );

  if (!isNativeEsignEnabled || !canUserManageNda)
    return <Navigate to={ndasPath} replace />;

  return (
    <AgreementSetup
      backPath={ndasPath}
      backLabel={dataRoomName}
      onActivate={createNda}
      onActivated={finishActivation}
      onDocusignConnected={disableNativeEsign}
    />
  );
};

export default DataRoomNdaSetupPage;
